import React, { useState } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, FileText, Calendar, Radio, Users, 
  Settings, LogOut, BookOpen, Mail, Share2, FileEdit, Store
} from 'lucide-react';
import ArticlesManager from './ArticlesManager';
import EventsManager from './EventsManager';
import MediaManager from './MediaManager';
import SubmissionsViewer from './SubmissionsViewer';
import AuthorsManager from './AuthorsManager';
import SettingsManager from './SettingsManager';
import SocialLinksManager from './SocialLinksManager';
import PagesManager from './PagesManager';
import BookFormatsManager from './BookFormatsManager';
import RetailersManager from './RetailersManager';

const AdminDashboard = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    onLogout();
    navigate('/admin/login');
  };

  const menuItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/dashboard/articles', icon: FileText, label: 'Articles' },
    { path: '/admin/dashboard/events', icon: Calendar, label: 'Events' },
    { path: '/admin/dashboard/media', icon: Radio, label: 'Media' },
    { path: '/admin/dashboard/pages', icon: FileEdit, label: 'Pages' },
    { path: '/admin/dashboard/pre-order', icon: BookOpen, label: 'Pre-Order' },
    { path: '/admin/dashboard/retailers', icon: Store, label: 'Other Retailers' },
    { path: '/admin/dashboard/submissions', icon: Mail, label: 'Submissions' },
    { path: '/admin/dashboard/authors', icon: Users, label: 'Authors' },
    { path: '/admin/dashboard/social', icon: Share2, label: 'Social Links' },
    { path: '/admin/dashboard/settings', icon: Settings, label: 'Settings' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#F4F1E8' }}>
      {/* Sidebar */}
      <div style={{
        width: '250px',
        backgroundColor: '#111111',
        color: '#FFFFFF',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1.5rem',
            fontWeight: '700',
            marginBottom: '4px'
          }}>
            Admin Panel
          </h2>
          <p style={{ fontSize: '0.85rem', color: '#999999' }}>
            The Goldilocks Zone
          </p>
        </div>

        <nav style={{ flex: 1 }}>
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                marginBottom: '8px',
                borderRadius: '4px',
                textDecoration: 'none',
                color: isActive(item.path) ? '#111111' : '#999999',
                backgroundColor: isActive(item.path) ? '#C9A24B' : 'transparent',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!isActive(item.path)) {
                  e.currentTarget.style.backgroundColor = '#222222';
                  e.currentTarget.style.color = '#FFFFFF';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive(item.path)) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#999999';
                }
              }}
            >
              <item.icon size={20} />
              <span style={{ fontSize: '0.95rem', fontWeight: '500' }}>{item.label}</span>
            </Link>
          ))}
        </nav>

        <button
          onClick={handleLogout}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 16px',
            borderRadius: '4px',
            border: 'none',
            backgroundColor: 'transparent',
            color: '#999999',
            cursor: 'pointer',
            fontSize: '0.95rem',
            fontWeight: '500',
            transition: 'all 0.2s ease',
            width: '100%'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#222222';
            e.target.style.color = '#FFFFFF';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = '#999999';
          }}
        >
          <LogOut size={20} />
          <span>Logout</span>
        </button>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, padding: '32px' }}>
        <Routes>
          <Route index element={<DashboardHome />} />
          <Route path="articles" element={<ArticlesManager />} />
          <Route path="events" element={<EventsManager />} />
          <Route path="media" element={<MediaManager />} />
          <Route path="pages" element={<PagesManager />} />
          <Route path="pre-order" element={<BookFormatsManager />} />
          <Route path="retailers" element={<RetailersManager />} />
          <Route path="submissions" element={<SubmissionsViewer />} />
          <Route path="authors" element={<AuthorsManager />} />
          <Route path="social" element={<SocialLinksManager />} />
          <Route path="settings" element={<SettingsManager />} />
        </Routes>
      </div>
    </div>
  );
};

const DashboardHome = () => {
  return (
    <div>
      <h1 style={{
        fontFamily: 'var(--font-display)',
        fontSize: '2.5rem',
        fontWeight: '700',
        marginBottom: '16px',
        color: '#111111'
      }}>
        Welcome to Admin Dashboard
      </h1>
      <p style={{
        fontSize: '1.1rem',
        color: '#666666',
        marginBottom: '32px'
      }}>
        Manage your website content from here.
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '24px',
        marginTop: '32px'
      }}>
        {[
          { label: 'Articles', icon: FileText, link: '/admin/dashboard/articles', color: '#111111' },
          { label: 'Events', icon: Calendar, link: '/admin/dashboard/events', color: '#111111' },
          { label: 'Media', icon: Radio, link: '/admin/dashboard/media', color: '#111111' },
          { label: 'Submissions', icon: Mail, link: '/admin/dashboard/submissions', color: '#111111' },
        ].map((card) => (
          <Link
            key={card.link}
            to={card.link}
            style={{
              padding: '32px',
              backgroundColor: '#FFFFFF',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              textDecoration: 'none',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
            }}
          >
            <card.icon size={32} color={card.color} style={{ marginBottom: '16px' }} />
            <h3 style={{
              fontSize: '1.3rem',
              fontWeight: '600',
              color: '#111111',
              marginBottom: '8px'
            }}>
              {card.label}
            </h3>
            <p style={{ fontSize: '0.9rem', color: '#666666' }}>
              Manage {card.label.toLowerCase()}
            </p>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default AdminDashboard;
